import { useEffect } from 'react';

/** Oeffentliche Basis-URL; Canonical- und Open-Graph-URLs muessen absolut sein. */
export const SITE_URL = 'https://www.canebase.ch';
const SHARE_IMAGE = `${SITE_URL}/canebase-logo-full.png`;

const OG_LOCALES: Record<string, string> = {
  de: 'de_CH',
  en: 'en_GB',
  fr: 'fr_CH',
  it: 'it_CH',
  nl: 'nl_NL',
};

/** Suchmaschinen kuerzen Beschreibungen bei ca. 155-160 Zeichen ohnehin - lieber selbst an der Wortgrenze. */
const shorten = (text: string, max = 160) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
};

export interface SeoOptions {
  title: string;
  description?: string;
  /** Pfad der Seite, z.B. "/pakete". Wird zur Canonical-URL - unbekannte Pfade, die die Startseite
   * rendern (Route "*"), verweisen so auf "/" statt als Duplikat zu zaehlen. */
  path: string;
  /** Aktive Sprache fuer og:locale. */
  lang?: string;
}

/** Setzt <meta>/<link> im Kopf; legt das Element an, falls index.html es nicht schon enthaelt. */
const upsert = (selector: string, create: () => HTMLElement, set: (el: HTMLElement) => void) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  set(el);
};

const setMeta = (attr: 'name' | 'property', key: string, content: string) =>
  upsert(
    `meta[${attr}="${key}"]`,
    () => {
      const el = document.createElement('meta');
      el.setAttribute(attr, key);
      return el;
    },
    (el) => el.setAttribute('content', content),
  );

/**
 * Pflegt Titel, Beschreibung, Canonical-URL sowie Open-Graph-/Twitter-Tags der oeffentlichen Seiten
 * clientseitig mit. Die Grundwerte stehen zusaetzlich statisch in dist/index.html bzw. dist/prerender/*
 * (siehe seoPages in vite.config.ts) - dort lesen sie auch Crawler ohne JavaScript.
 */
export function useSeo({ title, description, path, lang }: SeoOptions): void {
  useEffect(() => {
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;
    document.title = title;
    setMeta('property', 'og:title', title);
    setMeta('name', 'twitter:title', title);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', SHARE_IMAGE);
    setMeta('name', 'twitter:image', SHARE_IMAGE);
    if (description) {
      const short = shorten(description);
      setMeta('name', 'description', short);
      setMeta('property', 'og:description', short);
      setMeta('name', 'twitter:description', short);
    }
    if (lang && OG_LOCALES[lang]) setMeta('property', 'og:locale', OG_LOCALES[lang]);
    upsert(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        return el;
      },
      (el) => el.setAttribute('href', url),
    );
  }, [title, description, path, lang]);
}
